import React, { useEffect, useState } from "react";

import IsometricCube from "../molecules/IsometricCube";
import { roundedPolygonPath } from "../../data/svgPaths";

export type OperationsLayer = {
  key: string;
  label: string;
  note: string;
  color: string;
};

export const OPERATIONS_LAYERS: OperationsLayer[] = [
  {
    key: "customers",
    label: "Customers",
    note: "profiles & sales reps",
    color: "#1d4ed8",
  },
  {
    key: "orders",
    label: "Orders",
    note: "intake to fulfillment",
    color: "#0ea5e9",
  },
  {
    key: "inventory",
    label: "Inventory",
    note: "products & stock",
    color: "#0891b2",
  },
  {
    key: "carriers",
    label: "Carriers",
    note: "shipping & logistics",
    color: "#14b8a6",
  },
  {
    key: "communication",
    label: "Communication",
    note: "calls, SMS, email",
    color: "#6366f1",
  },
  {
    key: "reporting",
    label: "Reporting",
    note: "operational insight",
    color: "#7c3aed",
  },
];

const VIEW_X_WIDE = -100;
const VIEW_W_WIDE = 800;
const VIEW_X_COMPACT = 0;
const VIEW_W_COMPACT = 600;
const VIEW_H = 400;
const COMPACT_BREAKPOINT = "(max-width: 640px)";
const STACK_CENTER_X = 300;
const LABEL_LEFT_X = 70;
const LABEL_RIGHT_X = 530;
const CONNECTOR_GAP = 12;
const STACK_TOP_Y = 90;
const LAYER_SPACING = 40;
const TILE_HALF_W = 150;
const TILE_HALF_H = 70;
const CYCLE_MS = 2400;
const ENTRY_DURATION_MS = 1800;
const FLIP_DELAY_MS = ENTRY_DURATION_MS + 1500;
const FLIP_DURATION_MS = 1600;
const TILE_CORNER_R = 10;

const tilePath = (cy: number) =>
  roundedPolygonPath(
    [
      [STACK_CENTER_X, cy - TILE_HALF_H],
      [STACK_CENTER_X + TILE_HALF_W, cy],
      [STACK_CENTER_X, cy + TILE_HALF_H],
      [STACK_CENTER_X - TILE_HALF_W, cy],
    ],
    TILE_CORNER_R,
  );

const prefersReducedMotion = (): boolean => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

type IdleCallback = () => void;
interface IdleWindow extends Window {
  // eslint-disable-next-line no-unused-vars
  requestIdleCallback?: (cb: IdleCallback) => number;
  // eslint-disable-next-line no-unused-vars
  cancelIdleCallback?: (handle: number) => void;
}

export default function OperationsStack() {
  const [mounted, setMounted] = useState(false);
  const [phase, setPhase] = useState<"cube" | "stack">("cube");
  const [revealed, setRevealed] = useState(false);
  const [active, setActive] = useState(0);
  const [isCompact, setIsCompact] = useState(false);

  // Defer the heavy SVG until the browser is idle so the hero copy +
  // CTAs paint first. On slow phones this saves ~hundreds of ms of
  // first-paint blocking.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const w = window as IdleWindow;
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(() => setMounted(true));
      return () => w.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(() => setMounted(true), 200);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(COMPACT_BREAKPOINT);
    const update = () => setIsCompact(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!mounted) return undefined;
    if (prefersReducedMotion()) {
      setPhase("stack");
      setRevealed(true);
      return undefined;
    }
    const flip = setTimeout(() => setPhase("stack"), FLIP_DELAY_MS);
    return () => clearTimeout(flip);
  }, [mounted]);

  useEffect(() => {
    if (phase !== "stack" || prefersReducedMotion()) return undefined;
    const reveal = setTimeout(() => setRevealed(true), FLIP_DURATION_MS);
    return () => clearTimeout(reveal);
  }, [phase]);

  useEffect(() => {
    if (!revealed || prefersReducedMotion()) return undefined;

    const id = window.setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      setActive((prev) => (prev + 1) % OPERATIONS_LAYERS.length);
    }, CYCLE_MS);

    return () => window.clearInterval(id);
  }, [revealed]);

  const flipped = phase === "stack";
  const viewX = isCompact ? VIEW_X_COMPACT : VIEW_X_WIDE;
  const viewW = isCompact ? VIEW_W_COMPACT : VIEW_W_WIDE;
  const viewBox = `${viewX} 0 ${viewW} ${VIEW_H}`;

  if (!mounted) {
    return <div aria-hidden="true" className="h-full w-full" />;
  }

  return (
    <div className="relative h-full w-full [perspective:1400px]">
      <div
        data-testid="flip-wrapper"
        className={`relative h-full w-full transition-transform [transform-style:preserve-3d] ${
          flipped ? "[transform:rotateY(180deg)]" : ""
        }`}
        style={{
          transitionDuration: `${FLIP_DURATION_MS}ms`,
          transitionTimingFunction: "cubic-bezier(0.6, 0, 0.3, 1)",
        }}
      >
        <div
          data-testid="cube-face"
          className="absolute inset-0 animate-cube-enter [animation-fill-mode:both] [backface-visibility:hidden] motion-reduce:animate-none"
        >
          <div className="h-full w-full animate-float [animation-delay:1800ms] motion-reduce:animate-none">
            <IsometricCube viewBox={viewBox} />
          </div>
        </div>
        <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)]">
          <svg
            viewBox={viewBox}
            aria-hidden="true"
            className="h-full w-full text-slate-400 dark:text-slate-500"
          >
            <defs>
              <filter
                id="tile-glow"
                x="-50%"
                y="-50%"
                width="200%"
                height="200%"
              >
                <feGaussianBlur stdDeviation="6" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {OPERATIONS_LAYERS.map((layer, i) => {
              const cy = STACK_TOP_Y + i * LAYER_SPACING;
              const path = tilePath(cy);
              const isLeft = i % 2 === 0;
              const labelX = isLeft ? LABEL_LEFT_X : LABEL_RIGHT_X;
              const labelAnchor = isLeft ? "end" : "start";
              const connectorStartX = isLeft
                ? STACK_CENTER_X - TILE_HALF_W
                : STACK_CENTER_X + TILE_HALF_W;
              const connectorEndX = isLeft
                ? labelX + CONNECTOR_GAP
                : labelX - CONNECTOR_GAP;
              const isActive = revealed && i === active;
              let calloutOpacity = 0;
              if (revealed) calloutOpacity = isActive ? 1 : 0.3;
              return (
                <g key={layer.key} className="layer" data-layer-key={layer.key}>
                  <path
                    className="tile-fill transition-opacity duration-700 ease-out"
                    d={path}
                    fill={layer.color}
                    opacity={isActive ? 0.92 : 0}
                    filter={isActive ? "url(#tile-glow)" : undefined}
                  />
                  <path
                    className="tile-outline transition-all duration-700 ease-out"
                    d={path}
                    fill="none"
                    stroke={isActive ? layer.color : "currentColor"}
                    strokeWidth={1.25}
                    strokeOpacity={isActive ? 1 : 0.35}
                    strokeDasharray="4 4"
                  />
                  <g
                    className="callout transition-opacity duration-700 ease-out"
                    opacity={calloutOpacity}
                    transform={`translate(0, ${cy})`}
                    style={{ display: isCompact ? "none" : undefined }}
                  >
                    <line
                      x1={connectorStartX}
                      y1={0}
                      x2={connectorEndX}
                      y2={0}
                      stroke="currentColor"
                      strokeWidth={1}
                      strokeDasharray="3 3"
                      opacity={0.5}
                    />
                    <text
                      x={labelX}
                      y={-4}
                      textAnchor={labelAnchor}
                      className="fill-gray-700 text-[13px] font-medium dark:fill-gray-200"
                    >
                      {layer.label}
                    </text>
                    <text
                      x={labelX}
                      y={12}
                      textAnchor={labelAnchor}
                      className="fill-gray-500 text-[11px] dark:fill-gray-400"
                    >
                      {layer.note}
                    </text>
                  </g>
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {isCompact && revealed && (
        <div className="pointer-events-none absolute inset-x-0 bottom-2 text-center">
          <p
            key={`label-${OPERATIONS_LAYERS[active].key}`}
            className="animate-[fade-in_500ms_ease-out_both] text-base font-medium text-gray-900 dark:text-gray-100"
            style={{ color: OPERATIONS_LAYERS[active].color }}
          >
            {OPERATIONS_LAYERS[active].label}
          </p>
          <p
            key={`note-${OPERATIONS_LAYERS[active].key}`}
            className="animate-[fade-in_500ms_ease-out_both] text-xs text-gray-500 dark:text-gray-400"
          >
            {OPERATIONS_LAYERS[active].note}
          </p>
        </div>
      )}
    </div>
  );
}
